// Utilidades de pantalla del Pricer Datec (formatos, catálogos de formulario, márgenes de presentación).
// NO calcula plata de una cotización: eso es del motor compartido (`motor.generated.ts`). El motor viejo que vivía
// acá se borró en F3 T10 r2; su oráculo congelado quedó en `backend/tests/fixtures/pricer-datec/pdf-celdas-doradas.json` (F4 T11 retiró `pdf-filas`).

import type {
  PricerItem,
  PricerParams,
  BoletaCalculo,
  Subtotales,
} from '../types/pricer-datec.types';
import { pctParaFirma } from './motor.generated';

/**
 * Margen operativo (OBS-013/014, decisión de Mela del 12-oct): es el MARKUP,
 *   (P. sin IVA − Previsiones − Costo Almacén) / (P. sin IVA − Previsiones).
 * En fracción. Sin base devuelve 0. Espejo de `margenOperativoPct` del backend
 * (pricer-datec.service.ts), que es lo que miran las reglas de aprobación.
 */
export function margenOperativo(sinIVA: number, previsiones: number, costo: number): number {
  const base = sinIVA - previsiones;
  return base > 0 ? (base - costo) / base : 0;
}

/** El margen operativo del total: la misma fórmula con las sumas. */
export function margenOperativoTotal(
  s: Pick<Subtotales, 'totalSinIVA' | 'totalPrev' | 'totalCostoAlm'>
): number {
  return margenOperativo(s.totalSinIVA, s.totalPrev, s.totalCostoAlm);
}

/**
 * Un margen operativo (en fracción) tal como lo mira la regla: en %, redondeado a
 * 2 decimales con la MISMA función que decide quién firma (`pctParaFirma` del
 * motor: mitad hacia arriba, decisión 6c; 7,995 % se ve «8,00», no «7,99»). Es el
 * único formateador de «Margen op.» en pantalla: cabecera, pastilla de cada línea,
 * columna, subtotal y tarjeta del Resumen. Con 0 o 1 decimal, una línea en 19,96 %
 * se leía «20» y la regla «< 20» la frenaba.
 */
export function fmtMargenOp(fraccion: number): string {
  return pctParaFirma(fraccion).toFixed(2);
}

export interface SemaforoResult {
  color: string;
  bg: string;
  label: 'BAJO' | 'MEDIO' | 'OK' | 'EXCEDE' | 'TOTAL';
}

export function semaforoMargen(m: number): SemaforoResult {
  if (m < 0.1)
    return { color: 'hsl(var(--bloqueo))', bg: 'hsl(var(--bloqueo-suave))', label: 'BAJO' };
  if (m < 0.18)
    return { color: 'hsl(var(--alerta))', bg: 'hsl(var(--alerta-suave))', label: 'MEDIO' };
  if (m > 0.84)
    return { color: 'hsl(var(--bloqueo))', bg: 'hsl(var(--bloqueo-suave))', label: 'EXCEDE' };
  return { color: 'hsl(var(--ok))', bg: 'hsl(var(--ok-suave))', label: 'OK' };
}

export const fmtUSD = (n: number): string =>
  !isFinite(n) || isNaN(n)
    ? '—'
    : new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
        n
      );

export const fmtPct = (n: number): string => (!isFinite(n) || isNaN(n) ? '—' : `${n.toFixed(2)}%`);

// Formatea una fecha que vino del backend como ISO string (YYYY-MM-DDTHH:mm:ss.sssZ).
// Usamos componentes UTC para que en zonas horarias detrás de UTC (e.g. Bolivia UTC-4)
// no se muestre el día anterior cuando la fecha original era un día calendario sin hora.
export function fmtFecha(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

type BDRegistroLite = {
  lob: string;
  cebe: string;
  marca: string;
  garantia: number;
  nac: number;
  tipoFlete: string | null;
  pctFlete: number | null;
};

export function lookupBD(bdRegistros: BDRegistroLite[], lob: string, cebe: string, marca: string) {
  if (!lob || !cebe || !marca) return null;
  return bdRegistros.find((r) => r.lob === lob && r.cebe === cebe && r.marca === marca) ?? null;
}

export interface CamposDeMarca {
  pctGarantia: number;
  pctNacionalizacion: number;
  tipoFlete: string;
  pctFleteOverride: number | null;
}

/**
 * Los campos que salen del catálogo de la marca (garantía, nacionalización y
 * flete), o `null` si la marca no está parametrizada para ese LOB/CeBe.
 *
 * Única fuente de verdad de "elegir marca ⇒ completar estos campos": la usan
 * tanto el selector LOB/CeBe/Marca como el buscador de Datos Maestros, para
 * que no se desincronicen entre sí — es la misma regla que ya se aplicó una
 * sola vez para "la marca está parametrizada" (`lookupBD`).
 */
export function camposDeMarca(
  bdRegistros: BDRegistroLite[],
  lob: string,
  cebe: string,
  marca: string
): CamposDeMarca | null {
  const reg = lookupBD(bdRegistros, lob, cebe, marca);
  if (!reg) return null;
  return {
    pctGarantia: reg.garantia,
    pctNacionalizacion: reg.nac,
    // Sin parametrizar, el flete queda en NINGUNO y el ítem avisa, en vez de
    // inventar un %.
    tipoFlete: reg.tipoFlete ?? 'NINGUNO',
    // El % propio de la marca entra por el override, que ya tenía precedencia
    // sobre el % del tipo. La cadena de `calcularItem` no cambia.
    pctFleteOverride: reg.pctFlete ?? null,
  };
}

/**
 * El estado de los campos derivados cuando NO hay marca de la cual derivarlos.
 *
 * 🔴 Pasa al cambiar de LOB o de CeBe: la marca se limpia porque ya no
 * pertenece al nuevo par, pero antes los cuatro campos que había traído se
 * quedaban. El resultado era una pantalla que decía "Marca sin parametrizar —
 * cargá el valor en Datos Maestros" al lado de un 13% heredado de la marca
 * anterior, y `calcularItem` usaba ese 13%. Cuando los campos eran editables
 * alguien lo corregía a mano; ahora son de solo lectura y no hay forma.
 *
 * Los ceros de acá no inventan nada: son el mismo "no sé" que ya muestra
 * `CampoDerivado` con `parametrizado={false}`, y ahora el ítem lo dice.
 */
export const CAMPOS_SIN_MARCA: CamposDeMarca = {
  pctGarantia: 0,
  pctNacionalizacion: 0,
  tipoFlete: 'NINGUNO',
  pctFleteOverride: null,
};

export function cebesPorLob(
  bdRegistros: Array<{ lob: string; cebe: string }>,
  lob: string
): string[] {
  if (!lob) return [];
  const set = new Set<string>();
  for (const r of bdRegistros) if (r.lob === lob) set.add(r.cebe);
  return Array.from(set).sort();
}

export function marcasPorLobCebe(
  bdRegistros: Array<{ lob: string; cebe: string; marca: string }>,
  lob: string,
  cebe: string
): string[] {
  if (!lob || !cebe) return [];
  const set = new Set<string>();
  for (const r of bdRegistros) if (r.lob === lob && r.cebe === cebe) set.add(r.marca);
  return Array.from(set).sort();
}

export const itemDefault = (id: number, n: number): PricerItem => ({
  id,
  num: n,
  lob: '',
  cebe: '',
  marcaProducto: '',
  numParte: '',
  descripcion: '',
  tipoCompra: 'HW_EXT',
  cantidad: 1,
  costoUnitario: 0,
  pctDescuento: 0,
  tipoFlete: 'NINGUNO',
  pctNacionalizacion: 0,
  markup: 20,
  pctGarantia: 0,
  aniosGarantia: 4,
});

/** El día de hoy en Bolivia (UTC-4, sin horario de verano) como `aaaa-mm-dd`: el día UTC adelantaba la fecha entre las 20:00 y la medianoche. */
export const hoyEnBolivia = (ahora: Date = new Date()): string =>
  new Date(ahora.getTime() - 4 * 60 * 60 * 1000).toISOString().slice(0, 10);

export const paramsDefault = (): PricerParams => ({
  cliente: '',
  creadoPor: '',
  fecha: hoyEnBolivia(),
  validezDias: 10,
  tipoQuote: 'NUEVO',
  tcOficial: 6.96,
  // Toda cotización nace en dólares. El TC de presentación se llena recién si
  // alguien elige bolivianos.
  moneda: 'USD',
  tcPresentacion: null,
  /*
    El campo se llama «Factor IVA + IT» y venía en 87, que es SOLO el IVA:
    100 − 13. El IT (Impuesto a las Transacciones, 3 %) nunca entraba, así que
    la etiqueta prometía dos impuestos y el número cobraba uno.

    84 = 100 − 13 (IVA) − 3 (IT), que es lo que la etiqueta dice.

    Lo reportó Oswaldo Vaca Diez el 10-sep-2026: «El factor IVA + IT en los
    campos editables sale 87, debería ser 84».

    Esto SUBE el precio facturado: se divide entre un número menor. Solo afecta
    a cotizaciones NUEVAS — cada cotización guarda su propio snapshot de
    parámetros, así que lo ya emitido conserva el 87 con el que se calculó y no
    se reescribe ningún precio que un cliente ya recibió.
  */
  factorIVA: 84,
  pctObsolescencia: 0.5,
  pctPrevExtra: 5,
  pctMkt: 1,
  pctComisiones: 1,
  // Retención impositiva: 13% compras locales, 12.5% exterior (hoja NEW del Excel).
  retencionLocal: 13,
  retencionExterior: 12.5,
  factorUSA: 6.96,
  factorBO1: 6.96,
  factorBO2: 13,
  tipoGarantia: 'Ninguno',
});

/**
 * El monto de la boleta o póliza que realmente se cobra (T7b, A2): `totalCargado` (la suma de las boletas por
 * línea a centavos) y, en un cálculo que no lo trae, `totalCosto`. Con el redondeo apagado son iguales. Es el
 * que lleva el «incluye boleta» del documento y el «Costo Boleta» del Resumen, así el TOTAL cierra al centavo.
 */
export function boletaCobrada(
  b: Pick<BoletaCalculo, 'totalCosto'> & { totalCargado?: number }
): number {
  return b.totalCargado ?? b.totalCosto;
}
